import { Link } from "react-router-dom";
import PageHeader from "@/components/site/PageHeader";
import SectionHeader from "@/components/site/SectionHeader";
import InquiryForm from "@/components/site/InquiryForm";
import { Card } from "@/components/ui/card";
import {
  ShieldCheck,
  UserCheck,
  FileCheck2,
  Layers,
  Zap,
  Gauge,
  Building2,
} from "lucide-react";
import { IMAGES } from "@/lib/brand";
import { Button } from "@/components/ui/button";

const REASONS = [
  {
    icon: ShieldCheck,
    title: "Reliable workforce supply",
    desc: "Consistent deployment with vetted, verified candidates and active replacement support.",
  },
  {
    icon: UserCheck,
    title: "Disciplined employees",
    desc: "Workers trained on industrial discipline, safety, and adherence to plant protocols.",
  },
  {
    icon: FileCheck2,
    title: "Statutory compliance (PF & ESI)",
    desc: "Full compliance with PF, ESI, bonus, and overtime norms with documentation.",
  },
  {
    icon: Layers,
    title: "Flexible staffing models",
    desc: "12-hour 2-shift, 8-hour 3-shift, or custom — tailored to your operating windows.",
  },
  {
    icon: Zap,
    title: "Fast deployment",
    desc: "48–72 hour rapid mobilization for verified clients with phased scaling for large needs.",
  },
  {
    icon: Gauge,
    title: "Operational efficiency",
    desc: "Embedded supervision, attendance management, and welfare reduce client overhead.",
  },
  {
    icon: Building2,
    title: "Multi-industry expertise",
    desc: "Manufacturing, automotive, logistics, housekeeping, food processing, and more.",
  },
];

export default function CompanyBenefitsPage() {
  return (
    <div data-testid="company-benefits-page">
      <PageHeader
        eyebrow="For Companies"
        title="Why companies choose RK Enterprises"
        description="A single partner for compliant, disciplined, and on-time manpower across industries."
        image={IMAGES.corporateMeeting}
      />

      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="The advantage"
            title="Built for procurement, HR, and plant teams"
            description="Reduce administrative burden, improve plant productivity, and stay 100% compliant."
            testid="company-benefits-header"
          />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REASONS.map((r, i) => {
              const Icon = r.icon;
              return (
                <Card
                  key={r.title}
                  data-testid={`reason-card-${i}`}
                  className="rk-lift p-6 sm:p-7 bg-white border border-[#E2E8F0] rounded-sm"
                >
                  <div className="h-11 w-11 grid place-items-center rounded-sm bg-[#1E3A8A]/10 text-[#1E3A8A]">
                    <Icon className="h-5 w-5" />
                  </div>
                  <h3 className="mt-4 font-display font-bold text-[#0F172A] text-lg">
                    {r.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-[#475569]">
                    {r.desc}
                  </p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Hire Workforce Form */}
      <section
        id="hire"
        className="bg-[#F8FAFC] py-16 md:py-24"
        data-testid="hire-workforce-section"
      >
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Hire Workforce"
            title="Tell us your workforce requirement"
            description="Share your staffing requirements and our business team will respond within 24 working hours with a tailored proposal."
            align="center"
            testid="hire-workforce-header"
          />
          <div className="mt-12 p-6 sm:p-8 bg-white border border-[#E2E8F0] rounded-sm shadow-sm">
            <InquiryForm />
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-[#64748B]">
              Prefer a direct conversation?
            </p>
            <Button
              asChild
              variant="ghost"
              data-testid="company-benefits-call-cta"
              className="text-[#1E3A8A] hover:bg-[#1E3A8A]/5"
            >
              <Link to="/contact">Reach our contact page →</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
